/**
 * Page Navigation Custom Scrollbar
 * Handles custom scrollbar functionality for horizontal navigation sections
 */

function initPageNavigationScrollbar(sectionId) {
  const scrollContainer = document.querySelector(`#${sectionId} .page-navigation__links`);
  const scrollbar = document.querySelector(`#${sectionId} .page-navigation__scrollbar`);
  const scrollbarThumb = document.querySelector(`#${sectionId} .page-navigation__scrollbar-thumb`);
  
  // The scrollbar is optional (merchants can turn it off), so everything below
  // has to keep working when it isn't in the DOM.
  const hasScrollbar = Boolean(scrollbar && scrollbarThumb);

  if (!scrollContainer) return;

  // The section's inline script and the DOMContentLoaded sweep below both call
  // this, so bail out if the listeners are already attached.
  if (scrollContainer.dataset.pageNavigationInitialized === 'true') return;
  scrollContainer.dataset.pageNavigationInitialized = 'true';

  let isDragging = false;
  let startX = 0;
  let scrollLeft = 0;
  
  // Keyboard navigation for the scroll container
  function handleKeyboardNavigation(e) {
    const scrollAmount = 100; // pixels to scroll
    
    switch(e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        scrollContainer.scrollLeft -= scrollAmount;
        announceScrollPosition();
        break;
      case 'ArrowRight':
        e.preventDefault();
        scrollContainer.scrollLeft += scrollAmount;
        announceScrollPosition();
        break;
      case 'Home':
        e.preventDefault();
        scrollContainer.scrollLeft = 0;
        announceScrollPosition();
        break;
      case 'End':
        e.preventDefault();
        scrollContainer.scrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        announceScrollPosition();
        break;
    }
  }
  
  // Announce scroll position to screen readers
  function announceScrollPosition() {
    const scrollPercentage = Math.round((scrollContainer.scrollLeft / (scrollContainer.scrollWidth - scrollContainer.clientWidth)) * 100);
    if (hasScrollbar) scrollbar.setAttribute('aria-valuenow', scrollPercentage);
    
    // Create live region announcement
    let announcement = document.querySelector('.scroll-announcement');
    if (!announcement) {
      announcement = document.createElement('div');
      announcement.className = 'scroll-announcement visually-hidden';
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      document.body.appendChild(announcement);
    }
    announcement.textContent = `Scrolled to ${scrollPercentage}% of navigation`;
  }

  function updateScrollbar() {
    const scrollWidth = scrollContainer.scrollWidth;
    const clientWidth = scrollContainer.clientWidth;
    const scrollLeft = scrollContainer.scrollLeft;
    
    if (scrollWidth <= clientWidth) {
      scrollContainer.classList.remove('is-scrollable');
      if (!hasScrollbar) return;
      scrollbar.style.display = 'none';
      scrollbar.setAttribute('aria-hidden', 'true');
      return;
    }
    
    scrollContainer.classList.add('is-scrollable');
    if (!hasScrollbar) return;
    
    scrollbar.style.display = 'block';
    scrollbar.setAttribute('aria-hidden', 'false');
    
    // Use pixel-based calculations instead of percentages
    const scrollbarWidth = scrollbar.clientWidth;
    const thumbWidthPixels = (clientWidth / scrollWidth) * scrollbarWidth;
    const maxScrollLeft = scrollWidth - clientWidth;
    const maxThumbPosition = scrollbarWidth - thumbWidthPixels;
    
    // Calculate thumb position in pixels
    const scrollRatio = maxScrollLeft > 0 ? scrollLeft / maxScrollLeft : 0;
    const thumbPosition = scrollRatio * maxThumbPosition;
    
    // Apply styles using pixels
    scrollbarThumb.style.width = thumbWidthPixels + 'px';
    scrollbarThumb.style.transform = `translateX(${thumbPosition}px)`;
    
    // Update ARIA attributes
    const scrollPercentage = Math.round(scrollRatio * 100);
    scrollbar.setAttribute('aria-valuenow', scrollPercentage);
  }

  // Add keyboard navigation to scroll container
  scrollContainer.addEventListener('keydown', handleKeyboardNavigation);
  
  // Keyboard navigation for scrollbar
  function handleScrollbarKeyboard(e) {
    const scrollAmount = 50;
    
    switch(e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        scrollContainer.scrollLeft -= scrollAmount;
        break;
      case 'ArrowRight':
        e.preventDefault();
        scrollContainer.scrollLeft += scrollAmount;
        break;
      case 'Home':
        e.preventDefault();
        scrollContainer.scrollLeft = 0;
        break;
      case 'End':
        e.preventDefault();
        scrollContainer.scrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        break;
    }
  }
  
  if (hasScrollbar) scrollbar.addEventListener('keydown', handleScrollbarKeyboard);

  // Update scrollbar on scroll
  scrollContainer.addEventListener('scroll', updateScrollbar);

  // Handle scrollbar thumb drag
  if (hasScrollbar) {
    scrollbarThumb.addEventListener('mousedown', function(e) {
      isDragging = true;
      startX = e.clientX;
      scrollLeft = scrollContainer.scrollLeft;
      document.body.style.userSelect = 'none';
    });

    document.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
    
      const deltaX = e.clientX - startX;
      const scrollbarWidth = scrollbar.clientWidth;
      const thumbWidthPixels = (scrollContainer.clientWidth / scrollContainer.scrollWidth) * scrollbarWidth;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const maxThumbMovement = scrollbarWidth - thumbWidthPixels;
    
      // Simple 1:1 ratio between thumb movement and scroll movement
      const dragRatio = maxScrollLeft / maxThumbMovement;
      const newScrollLeft = Math.max(0, Math.min(scrollLeft + (deltaX * dragRatio), maxScrollLeft));
    
      scrollContainer.scrollLeft = newScrollLeft;
    });

    document.addEventListener('mouseup', function() {
      if (isDragging) {
        isDragging = false;
        document.body.style.userSelect = '';
      }
    });

    // Handle scrollbar track click
    scrollbar.addEventListener('click', function(e) {
      if (e.target === scrollbarThumb) return;
    
      const rect = scrollbar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const scrollbarWidth = scrollbar.clientWidth;
      const thumbWidthPixels = (scrollContainer.clientWidth / scrollContainer.scrollWidth) * scrollbarWidth;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const maxThumbMovement = scrollbarWidth - thumbWidthPixels;
    
      // Calculate where the center of the thumb should be
      const targetThumbCenter = Math.max(thumbWidthPixels / 2, Math.min(clickX, scrollbarWidth - thumbWidthPixels / 2));
      const thumbPosition = targetThumbCenter - thumbWidthPixels / 2;
      const scrollRatio = thumbPosition / maxThumbMovement;
    
      scrollContainer.scrollLeft = scrollRatio * maxScrollLeft;
    });
  }

  // Initial update
  updateScrollbar();
  
  // Update on window resize
  window.addEventListener('resize', updateScrollbar);

  // Drag the items to scroll (mouse only - touch already scrolls natively)
  const DRAG_THRESHOLD = 5;
  let isItemDragging = false;
  let hasDraggedItems = false;
  let itemDragStartX = 0;
  let itemDragStartScrollLeft = 0;

  function endItemDrag() {
    if (!isItemDragging) return;
    isItemDragging = false;
    scrollContainer.classList.remove('is-dragging');
    document.body.style.userSelect = '';
  }

  scrollContainer.addEventListener('pointerdown', function(e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    if (scrollContainer.scrollWidth <= scrollContainer.clientWidth) return;

    isItemDragging = true;
    hasDraggedItems = false;
    itemDragStartX = e.clientX;
    itemDragStartScrollLeft = scrollContainer.scrollLeft;
  });

  document.addEventListener('pointermove', function(e) {
    if (!isItemDragging) return;

    const deltaX = e.clientX - itemDragStartX;

    // Only take over once the pointer has clearly moved, so plain clicks
    // still reach the links underneath.
    if (!hasDraggedItems && Math.abs(deltaX) > DRAG_THRESHOLD) {
      hasDraggedItems = true;
      scrollContainer.classList.add('is-dragging');
      document.body.style.userSelect = 'none';
    }

    if (hasDraggedItems) {
      e.preventDefault();
      scrollContainer.scrollLeft = itemDragStartScrollLeft - deltaX;
    }
  });

  document.addEventListener('pointerup', endItemDrag);
  document.addEventListener('pointercancel', endItemDrag);

  // Swallow the click that ends a drag so the link doesn't navigate
  scrollContainer.addEventListener('click', function(e) {
    if (!hasDraggedItems) return;
    e.preventDefault();
    e.stopPropagation();
    hasDraggedItems = false;
  }, true);

  // Native image/link dragging fights the scroll drag
  scrollContainer.addEventListener('dragstart', function(e) {
    e.preventDefault();
  });

  // Initial focus management
  function manageFocus() {
    const links = scrollContainer.querySelectorAll('.page-navigation-link__content');
    links.forEach((link, index) => {
      link.addEventListener('focus', () => {
        // Ensure focused link is visible
        const linkRect = link.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        
        if (linkRect.left < containerRect.left) {
          scrollContainer.scrollLeft -= (containerRect.left - linkRect.left) + 20;
        } else if (linkRect.right > containerRect.right) {
          scrollContainer.scrollLeft += (linkRect.right - containerRect.right) + 20;
        }
      });
    });
  }
  
  manageFocus();

  // Return cleanup function if needed
  return function cleanup() {
    scrollContainer.removeEventListener('scroll', updateScrollbar);
    scrollContainer.removeEventListener('keydown', handleKeyboardNavigation);
    if (hasScrollbar) scrollbar.removeEventListener('keydown', handleScrollbarKeyboard);
    window.removeEventListener('resize', updateScrollbar);
    document.removeEventListener('pointerup', endItemDrag);
    document.removeEventListener('pointercancel', endItemDrag);
    delete scrollContainer.dataset.pageNavigationInitialized;
  };
}

// Auto-initialize all page navigation sections on DOM load
document.addEventListener('DOMContentLoaded', function() {
  const sections = document.querySelectorAll('.page-navigation-section');
  sections.forEach(section => {
    if (section.id) {
      initPageNavigationScrollbar(section.id);
    }
  });
});
